/**
 * Screen-space-feel popups drawn in WORLD space: floating damage numbers and
 * comic punches (¡POW!, SPLASH!). Each rises, fades, and pops in with a
 * brief scale overshoot — the "juice" of a hit landing.
 */

interface Popup {
  alive: boolean;
  x: number;
  y: number;
  vy: number;
  life: number;
  maxLife: number;
  text: string;
  color: string;
  fontPx: number;
  /** Punch scale: 1.6 → 1 in the first 120 ms, then normal. */
  punch: boolean;
  stroke: boolean;
}

export class Popups {
  private readonly pool: Popup[];
  private cursor = 0;

  constructor(private readonly capacity = 48) {
    this.pool = Array.from({ length: capacity }, () => ({
      alive: false,
      x: 0,
      y: 0,
      vy: 0,
      life: 0,
      maxLife: 1,
      text: '',
      color: '#fff',
      fontPx: 15,
      punch: false,
      stroke: false,
    }));
  }

  /** Drop everything (match restart). */
  clear(): void {
    for (const p of this.pool) p.alive = false;
  }

  private take(): Popup {
    for (let i = 0; i < this.capacity; i++) {
      const p = this.pool[(this.cursor + i) % this.capacity];
      if (!p.alive) {
        this.cursor = (this.cursor + i + 1) % this.capacity;
        return p;
      }
    }
    const p = this.pool[this.cursor];
    this.cursor = (this.cursor + 1) % this.capacity;
    return p;
  }

  private spawn(
    x: number,
    y: number,
    text: string,
    color: string,
    opts: { life?: number; fontPx?: number; punch?: boolean; stroke?: boolean; rise?: number } = {},
  ): void {
    const p = this.take();
    p.alive = true;
    p.x = x;
    p.y = y;
    p.vy = opts.rise ?? -46;
    p.maxLife = p.life = opts.life ?? 1.1;
    p.text = text;
    p.color = color;
    p.fontPx = opts.fontPx ?? 15;
    p.punch = opts.punch ?? false;
    p.stroke = opts.stroke ?? true;
  }

  /** Floating damage number above a hit character. */
  damage(x: number, y: number, amount: number): void {
    if (amount <= 0) return;
    this.spawn(x, y, `-${Math.round(amount)}`, '#ff6b5e', {
      fontPx: amount >= 25 ? 20 : 15,
      punch: amount >= 25,
      life: 1.2,
    });
  }

  /**
   * Comic punch — ¡POW!, SPLASH! — scale-popped, slightly bigger.
   * `opts` exists for the loud callouts: the turn banner is bigger than the
   * default punch and lingers past its 0.9 s.
   */
  punch(
    x: number,
    y: number,
    text: string,
    color = '#ffd76a',
    opts: { fontPx?: number; life?: number; rise?: number } = {},
  ): void {
    this.spawn(x, y, text, color, {
      punch: true,
      fontPx: opts.fontPx ?? 19,
      life: opts.life ?? 0.9,
      rise: opts.rise ?? -30,
    });
  }

  /** Small neutral note (heal, "+ammo", ability ready). */
  note(x: number, y: number, text: string, color = '#8ef59a'): void {
    this.spawn(x, y, text, color, { fontPx: 13, life: 1.0, punch: false });
  }

  update(dt: number): void {
    const d = Math.min(dt, 0.05);
    for (const p of this.pool) {
      if (!p.alive) continue;
      p.life -= d;
      if (p.life <= 0) {
        p.alive = false;
        continue;
      }
      p.y += p.vy * d;
      p.vy *= Math.max(0, 1 - 1.6 * d); // ease out the rise
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const p of this.pool) {
      if (!p.alive) continue;
      const t = p.life / p.maxLife;
      // Fade the last 40%; pop-in overshoot in the first 120 ms.
      const age = p.maxLife - p.life;
      const alpha = Math.min(1, t / 0.4);
      const pop = p.punch && age < 0.12 ? 1 + (1 - age / 0.12) * 0.6 : 1;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(p.x, p.y);
      ctx.scale(pop, pop);
      ctx.font = `800 ${p.fontPx}px system-ui`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (p.stroke) {
        // Outlines scale with type so a big callout stays legible on sand/snow.
        ctx.lineWidth = Math.max(3, p.fontPx * 0.16);
        ctx.strokeStyle = 'rgba(0,0,0,0.75)';
        ctx.strokeText(p.text, 0, 0);
      }
      ctx.fillStyle = p.color;
      ctx.fillText(p.text, 0, 0);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
}
